"use client"

import Image from "next/image"
import Link from "next/link"
import { ArrowLeft, Home, RefreshCw } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

type ErrorStateProps = {
  code: string
  eyebrow: string
  title: string
  description: string
  imageSrc: string
  imageAlt: string
  onRetry?: () => void
  compact?: boolean
}

export function ErrorState({
  code,
  eyebrow,
  title,
  description,
  imageSrc,
  imageAlt,
  onRetry,
  compact = false,
}: ErrorStateProps) {
  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back()
      return
    }

    window.location.assign("/")
  }

  return (
    <main
      className={cn(
        "relative grid overflow-hidden bg-background px-5 py-10 text-foreground",
        compact ? "min-h-[70vh] place-items-center rounded-3xl" : "min-h-dvh place-items-center",
      )}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        <div className="absolute -left-28 -top-28 size-80 rounded-full bg-primary-100/70 blur-3xl dark:bg-primary-900/20" />
        <div className="absolute -bottom-32 -right-24 size-96 rounded-full bg-accent-100/70 blur-3xl dark:bg-accent-900/10" />
      </div>

      <section className="relative z-10 mx-auto grid w-full max-w-5xl items-center gap-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12">
        <Image
          src={imageSrc}
          alt={imageAlt}
          width={720}
          height={480}
          priority
          className="mx-auto h-auto w-full max-w-md lg:order-2"
        />

        <div className="text-center lg:order-1 lg:text-left">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/10 bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
            <span className="text-accent-600">{code}</span>
            <span aria-hidden>·</span>
            {eyebrow}
          </div>
          <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
          <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-muted-foreground sm:text-base lg:mx-0">
            {description}
          </p>

          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row lg:justify-start">
            {onRetry ? (
              <Button onClick={onRetry} size="lg">
                <RefreshCw className="size-4" />
                Tentar novamente
              </Button>
            ) : (
              <Button asChild size="lg">
                <Link href="/">
                  <Home className="size-4" />
                  Ir para o início
                </Link>
              </Button>
            )}
            <Button type="button" variant="outline" size="lg" onClick={goBack}>
              <ArrowLeft className="size-4" />
              Voltar
            </Button>
          </div>

          {onRetry && (
            <Link href="/" className="mt-5 inline-block text-sm font-medium text-primary hover:underline">
              Ou volte para o início
            </Link>
          )}
        </div>
      </section>
    </main>
  )
}
